import React, { useRef, useState } from 'react';
import { ALLOWED_CENSUS_EXTENSIONS, MAX_CENSUS_FILE_SIZE } from '../constants';
import { cn } from '../../../utils/cn';

export interface FileUploadProps {
  label?: string;
  value?: File | null;
  onChange: (file: File | null) => void;
  errorText?: string;
  required?: boolean;
  accept?: string;
  helperText?: string;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  label = 'Upload File',
  value,
  onChange,
  errorText,
  required = false,
  accept = '.xls,.xlsx,.csv',
  helperText,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validateFile = (file: File): string | null => {
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!extension || !ALLOWED_CENSUS_EXTENSIONS.includes(extension)) {
      return `Invalid file type. Allowed: ${ALLOWED_CENSUS_EXTENSIONS.join(', ')}`;
    }

    if (file.size > MAX_CENSUS_FILE_SIZE) {
      return `File size exceeds ${MAX_CENSUS_FILE_SIZE / (1024 * 1024)}MB limit`;
    }

    return null;
  };

  const handleFileSelect = (file: File) => {
    const validationError = validateFile(file);
    if (validationError) {
      setError(validationError);
      onChange(null);
      return;
    }

    setError(null);
    onChange(file);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  const displayError = error || errorText;

  const inputId = label ? `file-upload-${label.toLowerCase().replace(/\s+/g, '-')}` : undefined;

  return (
    <div className="w-full">
      {label && (
        <label 
          htmlFor={inputId}
          className="block text-sm font-medium mb-1" 
          style={{ color: 'var(--color-text-primary)' }}
        >
          {label}
          {required && (
            <span className="ml-1" style={{ color: 'var(--color-error-500)' }}>
              *
            </span>
          )}
        </label>
      )}

      <div
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            fileInputRef.current?.click();
          }
        }}
        role="button"
        tabIndex={0}
        aria-label={label || "Upload file"}
        className={cn(
          'border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer',
          dragActive && 'border-[var(--color-primary-500)] bg-[var(--color-primary-50)]',
          displayError && 'border-[var(--color-error-500)]',
          !dragActive && !displayError && 'border-[var(--color-border-light)]'
        )}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          id={inputId}
          type="file"
          accept={accept}
          onChange={handleInputChange}
          className="hidden"
        />

        {value ? (
          <div className="space-y-2">
            <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
              {value.name}
            </p>
            <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
              {formatFileSize(value.size)}
            </p>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
              }}
              className="text-xs font-medium"
              style={{ color: 'var(--color-error-500)' }}
            >
              Remove
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              Drag and drop a file here, or click to select
            </p>
            <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
              Allowed: {ALLOWED_CENSUS_EXTENSIONS.join(', ')} (Max {MAX_CENSUS_FILE_SIZE / (1024 * 1024)}MB)
            </p>
          </div>
        )}
      </div>

      {displayError && (
        <p className="mt-1 text-xs" style={{ color: 'var(--color-error-500)' }}>
          {displayError}
        </p>
      )}

      {!displayError && helperText && (
        <p className="mt-1 text-xs opacity-70" style={{ color: 'var(--color-text-secondary)' }}>
          {helperText}
        </p>
      )}
    </div>
  );
};

